import queryString from 'query-string'

import type {
  IEquipmentGroup,
  IGroupIntegral,
  TEquipmentCreateGroup,
  TEquipmentGroupElement,
  TEquipmentUpdateGroup
} from '@web/entities/group'
import { ApiResource, ApiTag } from '@web/shared/constants/api.constants'
import { baseApi } from '@web/shared/infrastructure/api-base-query'
import type { TId } from '@web/shared/types/id.type'
import type { IPagination } from '@web/shared/types/paged-response.type'
import type { TQueryParams } from '@web/shared/types/query-params.type'

export const equipmentGroupApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroups: builder.query<
      IPagination<TEquipmentGroupElement>,
      TQueryParams<{ parentId: TId; onlyRoot: boolean }>
    >({
      query: (params) => {
        const queryParams = queryString.stringify({ ...params })
        return { url: `${ApiResource.EquipmentGroups}?${queryParams}` }
      },
      providesTags: [ApiTag.EquipmentGroups]
    }),

    createGroup: builder.mutation<{ id: TId }, TEquipmentCreateGroup>({
      query: (data) => ({
        url: ApiResource.EquipmentGroups,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [ApiTag.EquipmentGroups]
    }),

    getGroup: builder.query<IEquipmentGroup, { groupId: TId }>({
      query: ({ groupId }) => ({
        url: `${ApiResource.EquipmentGroups}/${groupId}`
      }),
      providesTags: [ApiTag.EquipmentGroup]
    }),

    updateGroup: builder.mutation<
      void,
      { groupId: string; data: TEquipmentUpdateGroup }
    >({
      query: ({ groupId, data }) => ({
        url: `${ApiResource.EquipmentGroups}/${groupId}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.EquipmentGroup, ApiTag.EquipmentGroups]
    }),

    deleteGroup: builder.mutation<null, TId>({
      query: (id) => ({
        url: `${ApiResource.EquipmentGroups}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.EquipmentGroups]
    }),

    updateGroupPaused: builder.mutation<
      void,
      { groupId: string; data: Pick<IEquipmentGroup, 'paused'> }
    >({
      query: ({ groupId, data }) => ({
        url: `${ApiResource.EquipmentGroups}/${groupId}/paused`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.EquipmentGroup]
    }),

    updateGroupReorder: builder.mutation<
      void,
      { groupId: string; data: { order: number; parentId: string | null } }
    >({
      query: ({ groupId, data }) => ({
        url: `${ApiResource.EquipmentGroups}/${groupId}/reorder`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.EquipmentGroup, ApiTag.EquipmentGroups]
    }),

    getGroupIntegralScheme: builder.query<IGroupIntegral, { groupId: TId }>({
      query: ({ groupId }) => ({
        url: `${ApiResource.EquipmentGroups}/${groupId}/integral-scheme`
      }),
      providesTags: [ApiTag.EquipmentGroup]
    })
  })
})

export const {
  useGetGroupQuery,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useUpdateGroupPausedMutation,
  useUpdateGroupReorderMutation,
  useDeleteGroupMutation,
  useGetGroupIntegralSchemeQuery
} = equipmentGroupApi
